<template>
  <div>
    <v-dialog
      overlay-color="black"
      overlay-opacity="0.7"
      persistent
      max-width="760"
      width="94vw"
      :value="show && !confirmingDecline"
      @input="$emit('update:show', $event)"
    >
      <v-card :dark="darkMode" class="tos-dialog-card">
        <v-card-title class="headline" :class="titleTextClass">
          Accept Terms to continue
        </v-card-title>
        <v-card-text :class="bodyTextClass">
          <p>
            To continue using DICOM Vision<sup>®</sup>, please review and accept the current
            Terms of Service.
          </p>
          <p>
            Your data remains protected under applicable EU data protection
            requirements. See also our
            <a
              class="tos-dialog-link"
              href="https://dicom.vision/web-privacy-policy-cookie"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy</a
            >
            for details.
          </p>
          <div class="tos-dialog-content">
            <div class="tos-dialog-scrollable" v-html="content"></div>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            text
            small
            rounded
            class="text-caption"
            @click="confirmingDecline = true"
          >
            Decline and sign out
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            rounded
            class="white--text px-6"
            @click="$emit('accept')"
          >
            Accept and continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      overlay-color="black"
      overlay-opacity="0.7"
      :value="show && confirmingDecline"
      @input="confirmingDecline = $event"
      max-width="480"
      width="92vw"
    >
      <v-card :dark="darkMode" class="tos-dialog-card">
        <v-card-title class="headline" :class="titleTextClass">
          Sign out?
        </v-card-title>
        <v-card-text :class="bodyTextClass">
          If you decline the current Terms of Service, you will be signed out
          and won't be able to use DICOM Vision<sup>®</sup> until you accept them.
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            text
            rounded
            color="error"
            class="px-4"
            @click="onDeclineConfirmed"
          >
            Sign out
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            rounded
            class="white--text px-6"
            @click="confirmingDecline = false"
          >
            Go back
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  model: {
    prop: "show",
    event: "update:show"
  },
  computed: {
    ...mapGetters(["darkMode"]),
    titleTextClass() {
      return this.darkMode ? "white--text" : "grey--text text--darken-3";
    },
    bodyTextClass() {
      return this.darkMode
        ? "grey--text text--lighten-1"
        : "grey--text text--darken-2";
    }
  },
  props: {
    show: { type: Boolean, required: true },
    content: { type: String, required: true },
    isUpdate: { type: Boolean, default: false },
    currentVersion: { type: String, default: "" }
  },
  data() {
    return {
      confirmingDecline: false
    };
  },
  methods: {
    onDeclineConfirmed() {
      this.confirmingDecline = false;
      this.$emit("decline");
    }
  },
  watch: {
    show(value) {
      if (!value) {
        this.confirmingDecline = false;
      }
    }
  }
};
</script>

<style lang="scss">
.v-dialog > .v-card.tos-dialog-card,
.v-card.tos-dialog-card {
  border-radius: 32px !important;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.tos-dialog-content,
.tos-content {
  margin: 1rem 0 0.5rem;
}

.tos-dialog-scrollable,
.tos-scrollable {
  --tos-scroll-track: var(--app-scrollbar-track, rgba(0, 0, 0, 0.06));
  --tos-scroll-thumb: var(--app-scrollbar-thumb, rgba(60, 60, 67, 0.45));
  --tos-scroll-thumb-hover: var(
    --app-scrollbar-thumb-hover,
    rgba(60, 60, 67, 0.65)
  );
  max-height: min(56vh, 420px);
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: var(--tos-scroll-thumb) var(--tos-scroll-track);
  padding: 1rem 1rem 1.1rem;
  border-radius: 4px;
  white-space: pre-wrap;
  line-height: 1.5;
  font-size: 0.92rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #ffffff;
  color: rgba(0, 0, 0, 0.87);
}

.tos-dialog-scrollable::-webkit-scrollbar,
.tos-scrollable::-webkit-scrollbar {
  width: 9px;
}

.tos-dialog-scrollable::-webkit-scrollbar-track,
.tos-scrollable::-webkit-scrollbar-track {
  background: var(--tos-scroll-track);
  border-radius: 999px;
}

.tos-dialog-scrollable::-webkit-scrollbar-thumb,
.tos-scrollable::-webkit-scrollbar-thumb {
  background: var(--tos-scroll-thumb);
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.tos-dialog-scrollable::-webkit-scrollbar-thumb:hover,
.tos-scrollable::-webkit-scrollbar-thumb:hover {
  background: var(--tos-scroll-thumb-hover);
  background-clip: padding-box;
}

.tos-dialog-card.theme--dark .tos-dialog-scrollable,
.tos-dialog-card.theme--dark .tos-scrollable,
.theme--dark .tos-scrollable {
  border-color: rgba(255, 255, 255, 0.2);
  background: #1f1f1f;
  color: rgba(255, 255, 255, 0.9);
}

.tos-dialog-link {
  text-decoration: underline;
}

.theme--dark .tos-dialog-link {
  color: #90caf9;
}
</style>
